import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { anchorRootAttrs } from '../../../../comments';
import type { ProfileTaskAutomation } from '../../api/profile-task-automation-adapter';
import type { useProfileTask } from '../../api/use-profile-task';
import type { ProfileTaskParentContext } from '../../model/profile-task';
import { profileTaskTranslationKeys } from '../../profile-task-translations';
import { ProfileTaskBreadcrumbs } from '../ProfileTaskBreadcrumbs';
import { ProfileTaskFields } from '../ProfileTaskFields';
import { ProfileTaskCommentsPublisher } from '../profile-task-comments-document';
import { TaskDetailHeading } from './TaskHeading';
import { TaskTileFrame } from './TaskTileFrame';

/**
 * Placeholder blocks mirroring the generated content layout (description, workflow steps,
 * required skills), shown while the task's content generation is still running.
 */
const GeneratingTaskPlaceholder: FunctionComponent = () => (
    <div aria-hidden={true} className="flex w-full flex-col gap-6xl" data-testid="profile-task-generating-placeholder">
        <div className="flex flex-col gap-s">
            <SkeletonBase className="h-4 w-full" />
            <SkeletonBase className="h-4 w-full" />
            <SkeletonBase className="h-4 w-2/3" />
        </div>
        <section className="flex flex-col gap-m">
            <SkeletonBase className="h-5 w-28" />
            {[0, 1, 2].map((step) => (
                <div key={step} className="flex flex-col gap-s rounded-xl bg-surface-subtle p-l">
                    <SkeletonBase className="h-4 w-1/2" />
                    <SkeletonBase className="h-3 w-full" />
                    <SkeletonBase className="h-3 w-5/6" />
                </div>
            ))}
        </section>
        <section className="flex flex-col gap-m">
            <SkeletonBase className="h-5 w-32" />
            <SkeletonBase className="h-8 w-full" />
            <SkeletonBase className="h-8 w-3/4" />
        </section>
    </div>
);

export const PersistedTaskTileContent: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly automation: ProfileTaskAutomation | undefined;
    readonly canComment: boolean;
    readonly comments?: { readonly isOpen: boolean; readonly onToggle: () => void };
    readonly context: ProfileTaskParentContext;
    readonly onClose: () => void;
    readonly task: NonNullable<ReturnType<typeof useProfileTask>['data']>;
}> = ({ activeLanguage, automation, canComment, comments, context, onClose, task }) => {
    const translate = useTranslate();
    const isGenerating = task.generationState === 'queued' || task.generationState === 'started';
    const commentable = {
        itemId: task.taskId,
        newThreadCreationDisabled: !canComment,
    };

    return (
        <TaskTileFrame
            comments={canComment ? comments : undefined}
            contentAttrs={anchorRootAttrs(task.taskId)}
            testId="profile-task-detail"
            onClose={onClose}
        >
            {canComment ? <ProfileTaskCommentsPublisher task={task} /> : null}
            <div className="flex flex-col gap-m">
                <ProfileTaskBreadcrumbs context={context} />
                <TaskDetailHeading commentItemId={canComment ? task.taskId : undefined} title={task.title} />
            </div>
            {task.generationState === 'failed' ? (
                <p className="m-0 text-sm leading-normal text-text-error">
                    {translate(profileTaskTranslationKeys.statusGenerationFailed)}
                </p>
            ) : isGenerating ? (
                <Fragment>
                    <p className="m-0 text-sm leading-normal text-text-secondary">
                        {translate(profileTaskTranslationKeys.draftHint)}
                    </p>
                    <GeneratingTaskPlaceholder />
                </Fragment>
            ) : (
                <ProfileTaskFields
                    activeLanguage={activeLanguage}
                    automation={automation}
                    commentable={canComment ? commentable : undefined}
                    context={context}
                    task={task}
                />
            )}
        </TaskTileFrame>
    );
};
